import { BannerCreate } from 'utils/campaign/requirementUtils/types';
import { BannerImagesData } from 'utils/clickme';
import { BannerImageCropType, BannerModerationStatus } from 'utils/constants/banner';
import { CampaignSource } from 'utils/constants/campaign';

import { TextTypography } from '@hh.ru/magritte-ui-typography/types';

import {
    BannerTextType,
    setBannerPreviousText,
    setBannerTextType,
    setBannerTextWasGenerated,
} from 'store/models/bannerTextGeneration';
import { CreateBannerState, CropInfo, Images } from 'store/models/createBanner';
import { AppDispatch } from 'store/utils';

export interface BannerStatistics {
    averagePricePerClickInCents?: number;
    amount: number;
    clicks: number;
    cost: number;
    ctr: number;
    maxClickDate: string | null;
    minClickDate: string | null;
    shows: number;
}

export interface BannerModerationState {
    moderationReason: string;
    source: CampaignSource;
    status: BannerModerationStatus;
}

export const getBannerTextTypography = (isMobile: boolean): TextTypography =>
    isMobile ? 'label-3-regular' : 'label-2-regular';

export interface Banner {
    createdAt: string;
    id: number;
    imageUrl: string;
    states: BannerModerationState[];
    statistics: BannerStatistics;
    text: string;
    title: string;
    url: string;
    vacancyId: number;
    enabled: boolean;
    moderationStatus: BannerModerationStatus;
    hasVideo: boolean;
    bannedAsAdvertisement: boolean;
    bannedForAllSources: boolean;
    videoThumbnailUrl: string;
}

type GetBannerCrop = (options: {
    cropContainerSize: number;
    imageHeight: number;
    imageWidth: number;
    cropAspect: number;
    cropStartX?: number;
    cropStartY?: number;
    originalCropWidth?: number;
    originalCropHeight?: number;
}) => CropInfo;

export const getBannerCrop: GetBannerCrop = ({
    cropContainerSize,
    imageWidth,
    imageHeight,
    cropAspect,
    cropStartX = 0,
    cropStartY = 0,
    originalCropWidth,
    originalCropHeight,
}) => {
    /** Пропорции ширины к высоте исходного изображения */
    const imageAspect = imageWidth / imageHeight;
    /**
     * Если картинка больше контейнера, то ограничиваем кропалку размерами контейнера,
     * иначе используем исходную картинку как есть.
     * */
    const cropMaxWidth = Math.min(imageWidth, cropContainerSize);
    const cropMaxHeight = Math.min(imageHeight, cropContainerSize);

    let cropWidth = cropMaxWidth;
    let cropHeight = cropMaxHeight;
    let cropHeightPercent = 100;
    let cropWidthPercent = 100;
    let cropX = 0;
    let cropY = 0;
    const cropXPercent = (cropStartX / imageWidth) * 100;
    const cropYPercent = (cropStartY / imageHeight) * 100;

    /** Для горизонтальных изображений */
    if (imageHeight < imageWidth) {
        /** Кроп может захватывать всю высоту изображения.
         *  Исходная картинка более вытянутая по горизонтали, чем кроп
         *  Пример: кроп 180х150, картинка 500х150
         * */
        if (cropAspect < imageAspect) {
            cropHeight = cropMaxWidth / imageAspect;
            cropWidth = cropHeight * cropAspect;
        } else {
            /**
             * Кроп может захватывать всю ширину изображения.
             * Пример: кроп 500х150, картинка 500х500
             * */
            cropHeight = cropWidth / cropAspect;
        }

        /** Преобразуем оригинальные координаты в координаты кропалки */
        if (cropStartX !== null && cropStartY !== null) {
            cropX = cropMaxWidth * (cropXPercent / 100);
            cropY = (cropMaxWidth / imageAspect) * (cropYPercent / 100);
        }
        /** Преобразуем сохранённые размеры кропа в размеры для кропалки */
        if (originalCropWidth && originalCropHeight) {
            cropHeight = (cropMaxWidth / imageAspect) * (originalCropHeight / imageHeight);
            cropWidth = cropHeight * cropAspect;
        }

        cropWidthPercent = (cropWidth / cropMaxWidth) * 100;
        cropHeightPercent = (cropHeight / (cropMaxWidth / imageAspect)) * 100;
    }

    /** Для вертикальных изображений */
    if (imageHeight >= imageWidth) {
        /** кроп более вытянутый по горизонтали, чем исходная картинка */
        if (cropAspect > imageAspect) {
            cropWidth = cropHeight * imageAspect;
            cropHeight = cropWidth / cropAspect;
        } else {
            cropWidth = cropHeight * cropAspect;
        }

        /** Преобразуем оригинальные координаты в координаты кропалки */
        if (cropStartX !== null && cropStartY !== null) {
            cropX = cropMaxHeight * imageAspect * (cropXPercent / 100);
            cropY = cropMaxHeight * (cropYPercent / 100);
        }

        /** Преобразуем сохранённые размеры кропа в размеры для кропалки */
        if (originalCropHeight && originalCropWidth) {
            cropWidth = cropMaxHeight * imageAspect * (originalCropWidth / imageWidth);
            cropHeight = cropWidth / cropAspect;
        }

        cropHeightPercent = (cropHeight / cropMaxHeight) * 100;
        cropWidthPercent = (cropWidth / (cropMaxHeight * imageAspect)) * 100;
    }

    return {
        crop: {
            unit: 'px',
            x: isFinite(cropX) ? Math.floor(cropX) : 0,
            y: isFinite(cropY) ? Math.floor(cropY) : 0,
            width: Math.floor(cropWidth),
            height: Math.floor(cropHeight),
        },
        percentCrop: {
            unit: '%',
            x: isFinite(cropXPercent) ? Math.floor(cropXPercent * 100) / 100 : 0,
            y: isFinite(cropYPercent) ? Math.floor(cropYPercent * 100) / 100 : 0,
            width: isFinite(cropWidthPercent) ? Math.floor(cropWidthPercent * 100) / 100 : 100,
            height: isFinite(cropHeightPercent) ? Math.floor(cropHeightPercent * 100) / 100 : 100,
        },
    };
};

type GetBannerImages = (images: Images, activeCropTypes: BannerImageCropType[]) => BannerImagesData[];

export const getBannerImages: GetBannerImages = (images, activeCropTypes) => {
    const bannerImages: BannerImagesData[] = [];
    const cropTypes = Object.keys(images) as BannerImageCropType[];
    cropTypes.forEach((cropType) => {
        const id = images[cropType]?.id;
        if (id && activeCropTypes.includes(cropType)) {
            bannerImages.push({
                originalImageId: id,
                imageCropType: cropType,
            } as BannerImagesData);
        }
    });
    return bannerImages;
};

export const getBannerForCampaign = (
    state: CreateBannerState,
    activeCropTypes: BannerImageCropType[]
): BannerCreate => {
    const { bannerTitle, bannerLink, bannerText, images, bannerVideo, classificationCodeId, isAdvertisement } = state;
    const bannerImages = getBannerImages(images, activeCropTypes);

    return {
        title: bannerTitle,
        url: bannerLink,
        text: bannerText,
        bannerImages,
        bannerVideo,
        classificationCodeId,
        isAdvertisement,
        bannedAsAdvertisement: false,
    };
};

export const handleBannerTextGenerationChange = (
    dispatch: AppDispatch,
    value: string,
    defaultText: string,
    bannerTextWasGenerated: boolean
): void => {
    dispatch(setBannerPreviousText(value));

    if (value === defaultText) {
        dispatch(setBannerTextType(BannerTextType.Default));
    } else {
        dispatch(setBannerTextType(BannerTextType.Edited));
    }

    if (bannerTextWasGenerated) {
        dispatch(setBannerTextWasGenerated(false));
    }
};
